// @ts-nocheck
import { ReactNode } from 'react';
import { Box, BackgroundImage } from '@mantine/core';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Stack, Center, Group } from '@/components/ui/layout';
import { Title, Text, Badge } from '@/components/ui/core';
import { FadeIn, SlideIn } from '@/components/animations';

export type SplitRatio = '50-50' | '40-60' | '60-40' | '35-65';
export type LeftContentType = 'intro' | 'image' | 'features' | 'testimonial' | 'gradient';

interface Feature {
  icon: ReactNode;
  title: string;
  description: string;
}

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  avatar?: string;
}

interface SplitScreenLayoutProps {
  children: ReactNode;
  /** Split ratio between left and right panels */
  ratio?: SplitRatio;
  /** Type of content for the left panel */
  leftContentType?: LeftContentType;
  /** Background image URL for image type */
  imageUrl?: string;
  /** Gradient colors for gradient type */
  gradientFrom?: string;
  gradientTo?: string;
  /** Title for the left panel */
  title?: string;
  /** Subtitle for the left panel */
  subtitle?: string;
  /** Badge text */
  badgeText?: string;
  /** Badge color */
  badgeColor?: string;
  /** Features list for features type */
  features?: Feature[];
  /** Testimonial for testimonial type */
  testimonial?: Testimonial;
  /** Logo component */
  logo?: ReactNode;
  /** Whether to reverse layout on mobile */
  reverseOnMobile?: boolean;
  /** Overlay opacity for image backgrounds */
  overlayOpacity?: number;
  /** Accent color for decorative elements */
  accentColor?: string;
}

export default function SplitScreenLayout({
  children,
  ratio = '50-50',
  leftContentType = 'intro',
  imageUrl,
  gradientFrom = 'indigo',
  gradientTo = 'cyan',
  title = 'Welcome Back',
  subtitle = 'Sign in to continue to your account',
  badgeText,
  badgeColor = 'indigo',
  features = [],
  testimonial,
  logo,
  reverseOnMobile = false,
  overlayOpacity = 0.7,
  accentColor = 'indigo',
}: SplitScreenLayoutProps) {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const getRatioStyles = () => {
    switch (ratio) {
      case '40-60':
        return { left: '40%', right: '60%' };
      case '60-40':
        return { left: '60%', right: '40%' };
      case '35-65':
        return { left: '35%', right: '65%' };
      default:
        return { left: '50%', right: '50%' };
    }
  };

  const ratioStyles = getRatioStyles();

  const renderIntroContent = () => (
    <Stack gap="xl" align="center" ta="center" maw={400}>
      {logo && (
        <FadeIn delay={100}>
          {logo}
        </FadeIn>
      )}
      {badgeText && (
        <FadeIn delay={150}>
          <Badge size="lg" color={badgeColor} variant="light">
            {badgeText}
          </Badge>
        </FadeIn>
      )}
      <SlideIn direction="up" delay={200}>
        <Title order={1} c="white" ta="center">
          {title}
        </Title>
      </SlideIn>
      <FadeIn delay={300}>
        <Text c="white" ta="center" size="lg" opacity={0.9}>
          {subtitle}
        </Text>
      </FadeIn>
      {/* Decorative dots */}
      <FadeIn delay={400}>
        <Group gap="xs" mt="md">
          {[...Array(3)].map((_, i) => (
            <Box
              key={i}
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: i === 1 ? 'white' : 'rgba(255,255,255,0.4)',
              }}
            />
          ))}
        </Group>
      </FadeIn>
    </Stack>
  );

  const renderFeaturesContent = () => (
    <Stack gap="xl" maw={400}>
      {logo && (
        <FadeIn delay={100}>
          {logo}
        </FadeIn>
      )}
      <SlideIn direction="up" delay={150}>
        <Title order={2} c="white">
          {title}
        </Title>
      </SlideIn>
      <FadeIn delay={200}>
        <Text c="white" opacity={0.9}>
          {subtitle}
        </Text>
      </FadeIn>
      <Stack gap="lg" mt="md">
        {features.map((feature, index) => (
          <SlideIn key={index} direction="left" delay={300 + index * 100}>
            <Group gap="md" align="flex-start">
              <Center
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: theme.radius.md,
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  flexShrink: 0,
                }}
              >
                {feature.icon}
              </Center>
              <div>
                <Text c="white" fw={600} size="sm">
                  {feature.title}
                </Text>
                <Text c="white" opacity={0.8} size="xs">
                  {feature.description}
                </Text>
              </div>
            </Group>
          </SlideIn>
        ))}
      </Stack>
    </Stack>
  );

  const renderTestimonialContent = () => (
    <Stack gap="xl" maw={400} align="center">
      {logo && (
        <FadeIn delay={100}>
          {logo}
        </FadeIn>
      )}
      {testimonial && (
        <>
          <SlideIn direction="up" delay={200}>
            <Text
              c="white"
              size="xl"
              ta="center"
              style={{ fontStyle: 'italic', lineHeight: 1.6 }}
            >
              "{testimonial.quote}"
            </Text>
          </SlideIn>
          <FadeIn delay={300}>
            <Stack gap="xs" align="center">
              {testimonial.avatar && (
                <Box
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '3px solid rgba(255,255,255,0.3)',
                  }}
                >
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.author}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
              )}
              <Text c="white" fw={600}>
                {testimonial.author}
              </Text>
              <Text c="white" opacity={0.8} size="sm">
                {testimonial.role}
              </Text>
            </Stack>
          </FadeIn>
        </>
      )}
    </Stack>
  );

  const renderLeftContent = () => {
    switch (leftContentType) {
      case 'features':
        return renderFeaturesContent();
      case 'testimonial':
        return renderTestimonialContent();
      default:
        return renderIntroContent();
    }
  };

  const getLeftBackground = () => {
    if (leftContentType === 'image' && imageUrl) {
      return undefined; // Will use BackgroundImage
    }
    if (leftContentType === 'gradient' || !imageUrl) {
      return `linear-gradient(135deg, var(--mantine-color-${gradientFrom}-6) 0%, var(--mantine-color-${gradientTo}-8) 100%)`;
    }
    return undefined;
  };

  const leftPanel = (
    <Box
      style={{
        width: ratioStyles.left,
        minHeight: '100vh',
        position: 'relative',
        display: 'none',
        background: getLeftBackground(),
      }}
      visibleFrom="md"
    >
      {leftContentType === 'image' && imageUrl ? (
        <>
          <BackgroundImage
            src={imageUrl}
            style={{
              position: 'absolute',
              inset: 0,
            }}
          />
          <Box
            style={{
              position: 'absolute',
              inset: 0,
              background: `linear-gradient(135deg, var(--mantine-color-${gradientFrom}-9) 0%, var(--mantine-color-${gradientTo}-9) 100%)`,
              opacity: overlayOpacity,
            }}
          />
        </>
      ) : null}

      {/* Decorative pattern */}
      <Box
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px)`,
          backgroundSize: '24px 24px',
          pointerEvents: 'none',
        }}
      />

      <Center h="100%" p="xl" style={{ position: 'relative', zIndex: 1 }}>
        {renderLeftContent()}
      </Center>
    </Box>
  );

  const rightPanel = (
    <Box
      style={{
        flex: 1,
        minHeight: '100vh',
        backgroundColor: isDark ? theme.colors.dark[7] : theme.white,
      }}
    >
      <Center mih="100vh" p="xl">
        {children}
      </Center>
    </Box>
  );

  return (
    <Box style={{ minHeight: '100vh', display: 'flex' }}>
      {leftPanel}
      {rightPanel}
    </Box>
  );
}
